import type {ExtendedLastUpdate} from '@domain/material-demand'
import {ForecastUpdatedVia} from '@domain/material-demand'
import {dataTestId} from '@hconnect/uikit/src/common'
import {Person, MailOutlined} from '@mui/icons-material'
import {Tooltip, Typography, Stack} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useMaterialDemandEdit} from '../../providers'

import {
  MaterialDemandTableFieldEdit,
  type OnEditMaterialDemandFn
} from './MaterialDemandTableFieldEdit'
import {MaterialDemandTableFieldTooltipContent} from './MaterialDemandTableFieldTooltipContent'
import {formatDemandValue} from './tableHelpers'

function EditedMarkIcon({updatedVia}: {updatedVia: ForecastUpdatedVia}) {
  const props = {fontSize: 'small' as const, sx: {mr: 0.5, fontSize: 'inherit'}}

  switch (updatedVia) {
    case ForecastUpdatedVia.User:
      return <Person {...props} {...dataTestId('value_overriden_by_user_icon')} />
    case ForecastUpdatedVia.Mail:
      return <MailOutlined {...props} {...dataTestId('value_overriden_by_mail_icon')} />
    default:
      throw new Error(`Invalid updatedVia ${updatedVia}`)
  }
}

interface TMaterialDemandTableFieldParams {
  mergedValue: number | undefined
  predictedValue: number | undefined
  lastUpdate: ExtendedLastUpdate | undefined
  onEdit?: OnEditMaterialDemandFn
  materialId: number | null
}

export function MaterialDemandTableField({
  mergedValue,
  predictedValue,
  lastUpdate,
  onEdit,
  materialId
}: TMaterialDemandTableFieldParams) {
  const {isEditing} = useMaterialDemandEdit()
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)

  if (isEditing && onEdit && materialId !== null && mergedValue !== undefined) {
    return (
      <MaterialDemandTableFieldEdit value={mergedValue} onEdit={onEdit} materialId={materialId} />
    )
  }

  const formattedValue = formatDemandValue(mergedValue, language)

  if (!lastUpdate || materialId === null || mergedValue === undefined) return <>{formattedValue}</>

  return (
    <Tooltip
      title={
        <MaterialDemandTableFieldTooltipContent
          lastUpdate={lastUpdate}
          predicted={predictedValue}
        />
      }
    >
      <Stack direction="column" display="inline-flex" alignItems="start">
        {formattedValue}
        <Stack direction="row" alignItems="start">
          <Typography variant="caption" display="flex" alignItems="center">
            <EditedMarkIcon updatedVia={lastUpdate.updatedVia} />
            {t('demand.edited')}
          </Typography>
        </Stack>
      </Stack>
    </Tooltip>
  )
}
